import React, { useEffect, useRef, useState } from 'react'
import {
  View,
  FlatList,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Keyboard,
  Platform,
  ScrollView,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import {
  Screen,
  Text,
  Icon,
  Header,
  HiddenText,
  KeyboardAwareView,
  FeatureTooltip,
  BottomModalBase,
  DeleteModal,
  type IconName,
} from '@components'
import { useLanguage } from '@context'
import { useChatbot, type ChatMessage } from './hook'
import { describeAction } from './describeAction'
import { getStyle } from './style'
import { useScreen, useKeyboardOffset } from '@hooks'
import { useFtChatbotConversations } from '../../hooks/api/queries/useFtChatbotConversations'
import { formatNoteDate } from '@utils'

// Share of the phone screen the history sheet may take.
const HISTORY_HEIGHT_RATIO = 0.75

export const Chatbot = () => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { isWeb, isMobile } = useScreen()
  const keyboardOffset = useKeyboardOffset()
  const { t } = useLanguage()
  const flatListRef = useRef<FlatList>(null)
  const containerRef = useRef<View | null>(null)
  const inputBarRef = useRef<View | null>(null)
  const [webPaddingBottom, setWebPaddingBottom] = useState(0)
  const [nativeKeyboardPadding, setNativeKeyboardPadding] = useState(0)
  const [height, setHeight] = useState(40)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [conversationToDelete, setConversationToDelete] = useState<string | undefined>()
  // Asked only once the history is opened.
  const {
    data: conversations = [],
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useFtChatbotConversations(isHistoryOpen)

  useEffect(() => {
    if (!isWeb) return
    if (typeof window === 'undefined' || !window.visualViewport) return

    const vv = window.visualViewport
    const update = () => {
      const node = containerRef.current as unknown as HTMLElement | null
      if (!node) return
      const containerBottom = node.getBoundingClientRect().bottom
      const viewportBottom = vv.height + vv.offsetTop
      setWebPaddingBottom(Math.max(0, containerBottom - viewportBottom))
    }

    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [isWeb])

  // iOS only: the window does NOT resize when the keyboard opens, so we
  // lift the input bar ourselves. We measure its real on-screen position
  // and pad by exactly its overlap with the keyboard — this stays correct
  // regardless of the bottom tab bar / safe-area, unlike a fixed offset.
  //
  // Android is not handled here either: Android 15/16 enforce edge-to-edge,
  // so `adjustResize` no longer shrinks the window when the keyboard opens
  // (see android/.../styles.xml) and the input bar stayed under the keyboard.
  // `KeyboardAwareView androidOnly` below shrinks the screen instead, as on
  // the event forms. iOS and web go through it untouched, so nothing is
  // counted twice with the padding computed here.
  // Web is handled by the visualViewport effect above.
  useEffect(() => {
    if (Platform.OS !== 'ios') return

    const onShow = Keyboard.addListener('keyboardWillShow', (e) => {
      const keyboardTop = e.endCoordinates.screenY
      // `nativeKeyboardPadding` is always 0 here (reset on hide), so the
      // measured bottom is the input bar's un-padded resting position.
      inputBarRef.current?.measureInWindow((_x, y, _width, measuredHeight) => {
        const overlap = Math.max(0, y + measuredHeight - keyboardTop)
        setNativeKeyboardPadding(overlap)
      })
    })
    const onHide = Keyboard.addListener('keyboardWillHide', () => {
      setNativeKeyboardPadding(0)
    })

    return () => {
      onShow.remove()
      onHide.remove()
    }
  }, [])

  const {
    messages,
    inputText,
    setInputText,
    isThinking,
    pendingActions,
    pendingMessageId,
    sendMessage,
    confirmAction,
    cancelAction,
    startNewConversation,
    openConversation,
    deleteConversation,
    hasDeleteFailed,
    clearDeleteFailure,
  } = useChatbot()

  const handleOpenConversation = (id: string) => {
    setIsHistoryOpen(false)
    void openConversation(id)
  }

  const handleSend = () => {
    sendMessage()
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true })
    }, 100)
  }

  // Starter prompts shown on the empty assistant screen. Tapping one sends it
  // straight away; they disappear once the conversation has any message.
  const suggestions: { icon: IconName; key: string }[] = [
    { icon: 'note', key: 'chatbot.suggestions.notes' },
    { icon: 'messageCircle', key: 'chatbot.suggestions.conversations' },
    { icon: 'work', key: 'chatbot.suggestions.sector' },
    { icon: 'calendar', key: 'chatbot.suggestions.calendar' },
    { icon: 'search', key: 'chatbot.suggestions.findContact' },
  ]

  const handleSuggestionPress = (text: string) => {
    sendMessage(text)
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true })
    }, 100)
  }

  const renderMessage = ({ item }: { item: ChatMessage }) => {
    if (item.role === 'user') {
      return (
        <View style={styles.userBubble}>
          <Text type="body" style={styles.userBubbleText}>
            {item.content}
          </Text>
        </View>
      )
    }

    // Only the message that brought the pending actions shows a card, and
    // the card lists exactly what its button sends.
    const showConfirmCard = item.id === pendingMessageId && pendingActions.length > 0

    return (
      <View>
        {item.content.trim() !== '' && (
          <View style={styles.aiBubble} testID="chatbot-ai-bubble">
            <Text type="body" style={styles.aiBubbleText}>
              {item.content}
            </Text>
          </View>
        )}
        {showConfirmCard && (
          <View style={styles.confirmCard} testID="chatbot-confirm-card">
            {pendingActions.map((action, idx) => (
              <Text key={idx} type="body" style={styles.confirmDescription}>
                {describeAction(t, action)}
              </Text>
            ))}
            <View style={styles.confirmButtons}>
              <TouchableOpacity
                style={styles.confirmButton}
                onPress={confirmAction}
                activeOpacity={0.7}
              >
                <Text type="bodyBold" style={styles.confirmButtonText}>
                  {t('chatbot.confirm')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={cancelAction}
                activeOpacity={0.7}
              >
                <Text type="bodyBold" style={styles.cancelButtonText}>
                  {t('chatbot.cancel')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    )
  }

  const isKeyboardOpen = keyboardOffset > 0

  const renderEmpty = () =>
    isKeyboardOpen ? null : (
      <View style={styles.welcomeContainer}>
        <Icon name="star" size={40} color={theme.colors.primary} />
        <Text type="body" style={styles.welcomeText}>
          {t('chatbot.welcome')}
        </Text>
        <Text type="bodySmall" style={styles.suggestionsTitle}>
          {t('chatbot.suggestionsTitle')}
        </Text>
        <View style={styles.suggestionsWrap}>
          {suggestions.map((s) => (
            <TouchableOpacity
              key={s.key}
              style={styles.suggestionChip}
              activeOpacity={0.7}
              onPress={() => handleSuggestionPress(t(s.key))}
            >
              <Icon name={s.icon} size={theme.iconSize.small} color={theme.colors.primary} />
              <Text type="bodySmall" style={styles.suggestionChipText}>
                {t(s.key)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    )

  return (
    <Screen noPaddingBottom>
      <KeyboardAwareView isFullScreen androidOnly offsetTopInset>
        <View
          ref={containerRef}
          style={[
            styles.container,
            isWeb && { paddingBottom: webPaddingBottom },
            !isWeb && { paddingBottom: nativeKeyboardPadding },
          ]}
        >
          <Header
            title={t('chatbot.title')}
            right={
              <View style={styles.headerActions}>
                <TouchableOpacity
                  testID="chatbot-history"
                  accessibilityRole="button"
                  onPress={() => {
                    clearDeleteFailure()
                    setIsHistoryOpen(true)
                  }}
                  style={styles.historyAction}
                >
                  <Text type="bodySmallBold" color={theme.colors.primary}>
                    {t('chatbot.history')}
                  </Text>
                </TouchableOpacity>
                {/* Same testID as the Header's own right icon: the e2e flows look for it. */}
                <Icon
                  testID="header-icon-right"
                  name="plus"
                  onPress={startNewConversation}
                  color={theme.colors.black}
                />
              </View>
            }
          />
          <FeatureTooltip
            id="chatbot_intro"
            title={t('featureTooltip.chatbot_intro.title')}
            description={t('featureTooltip.chatbot_intro.description')}
            icon="star"
          />
          <FlatList
            ref={flatListRef}
            data={messages}
            renderItem={renderMessage}
            keyExtractor={(item) => item.id}
            style={styles.messageList}
            contentContainerStyle={[
              styles.messageListContent,
              messages.length === 0 && { flex: 1, justifyContent: 'center' },
            ]}
            ListEmptyComponent={renderEmpty}
            onContentSizeChange={() => {
              if (messages.length > 0) {
                flatListRef.current?.scrollToEnd({ animated: true })
              }
            }}
          />
          {isThinking && (
            <View style={styles.typingContainer}>
              <ActivityIndicator size="small" color={theme.colors.primary} />
              <Text type="bodySmall" style={styles.typingText}>
                {t('chatbot.thinking')}
              </Text>
            </View>
          )}
          <HiddenText text={inputText} setHeight={setHeight} />
          <View ref={inputBarRef} style={styles.inputBar}>
            <TextInput
              style={[styles.textInput, isWeb && { height }]}
              value={inputText}
              onChangeText={setInputText}
              placeholder={t('chatbot.placeholder')}
              placeholderTextColor={theme.colors.grey50}
              multiline
              onSubmitEditing={handleSend}
              editable={!isThinking}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                {
                  backgroundColor:
                    inputText.trim() && !isThinking ? theme.colors.primary : theme.colors.grey30,
                },
              ]}
              onPress={handleSend}
              disabled={!inputText.trim() || isThinking}
              activeOpacity={0.7}
            >
              <Icon name="send" size={20} color={theme.colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAwareView>
      <BottomModalBase
        isVisible={isHistoryOpen}
        setIsVisible={setIsHistoryOpen}
        largePaddingTop={false}
        maxHeightRatio={HISTORY_HEIGHT_RATIO}
      >
        {/* On a computer the window would otherwise take the width of the longest title. */}
        <View
          style={[styles.historySheet, !isMobile && styles.historySheetDesktop]}
          testID="chatbot-history-sheet"
        >
          <Text type="subtitleBold">{t('chatbot.history')}</Text>
          {/* On a phone the error banner is drawn behind this sheet: the
              failure is said here. The website shows its banner as before. */}
          {hasDeleteFailed && Platform.OS !== 'web' && (
            <Text type="bodySmall" color={theme.colors.error} testID="chatbot-history-delete-error">
              {t('chatbot.deleteConversationError')}
            </Text>
          )}
          {isHistoryLoading ? (
            <ActivityIndicator size="small" color={theme.colors.primary} />
          ) : isHistoryError ? (
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('chatbot.error')}
            </Text>
          ) : conversations.length === 0 ? (
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('chatbot.historyEmpty')}
            </Text>
          ) : (
            <ScrollView style={styles.historyList}>
              {conversations.map((conversation) => (
                <View key={conversation.id} style={styles.historyRow}>
                  <TouchableOpacity
                    testID={`chatbot-history-item-${conversation.id}`}
                    accessibilityRole="button"
                    style={styles.historyRowText}
                    onPress={() => handleOpenConversation(conversation.id)}
                    activeOpacity={0.7}
                  >
                    <Text type="body" numberOfLines={1}>
                      {conversation.title?.trim() || t('chatbot.untitled')}
                    </Text>
                    <Text type="bodySmall" color={theme.colors.greyBlue}>
                      {formatNoteDate(conversation.updatedAt ?? conversation.createdAt, {
                        today: t('notes.dates.today'),
                        yesterday: t('notes.dates.yesterday'),
                      })}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID={`chatbot-history-delete-${conversation.id}`}
                    accessibilityRole="button"
                    accessibilityLabel={t('deleteModal.delete')}
                    style={styles.historyDelete}
                    onPress={() => setConversationToDelete(conversation.id)}
                  >
                    <Icon name="trash" size={theme.iconSize.small} color={theme.colors.error} />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          )}
        </View>
        {/* Inside the sheet: on a phone, a second window opens only from the one on screen. */}
        <DeleteModal
          isVisible={conversationToDelete !== undefined}
          setIsVisible={() => setConversationToDelete(undefined)}
          title=""
          description={t('chatbot.deleteConversationTitle')}
          onDelete={() => {
            if (conversationToDelete) deleteConversation(conversationToDelete)
          }}
        />
      </BottomModalBase>
    </Screen>
  )
}
